import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Autocomplete, Grid, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { ConfigContext } from 'components/ConfigGuard';
import {
  getMaxLengthValidationRule,
  getRequiredValidationRule,
  FormProps,
  getAddressControlName,
  countryFieldName,
} from 'util/Form';
import { defaultGridItemProps } from 'util/Layout';
import CountryFormItem from './CountryFormItem';
import { toAlpha3, countryName, DOMESTIC_CODES } from 'util/CountryCodes';

import externalStyles from 'components/profile/EditProfilePageView/EditProfileForm/EditProfileForm.module.scss';

interface SmartySuggestion {
  street_line: string;
  city: string;
  state: string;
  zipcode: string;
}

interface SmartyInternationalCandidate {
  street: string;
  locality: string;
  administrative_area: string;
  postal_code: string;
}

interface AddressFormSectionProps extends FormProps {
  addressFieldName?: string;
}


/** Formats a Smarty suggestion into a single display string for the street1 input and dropdown options. */
const formatSuggestion = (s: SmartySuggestion | SmartyInternationalCandidate, isUs: boolean): string => {
  if (isUs) {
    const us = s as SmartySuggestion;
    return `${us.street_line}, ${us.city}, ${us.state} ${us.zipcode}`;
  }
  const intl = s as SmartyInternationalCandidate;
  return [intl.street, intl.locality, [intl.administrative_area, intl.postal_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
};

const AddressFormSection: React.FunctionComponent<AddressFormSectionProps> = (props) => {
  const {
    inputProps,
    rowProps = defaultGridItemProps,
    required = false,
    disabled = false,
    addressFieldName = 'address',
  } = props;

  const { control, setValue } = useFormContext();
  const { smartyEmbeddedKey } = useContext(ConfigContext);
  const { enqueueSnackbar } = useSnackbar();

  const getControlName = useCallback(
    (label: string) => getAddressControlName(addressFieldName, label),
    [addressFieldName]
  );

  const countryValue: string = useWatch({ control, name: getControlName(countryFieldName) });
  const cityValue: string = useWatch({ control, name: getControlName('city') });
  const stateValue: string = useWatch({ control, name: getControlName('stateRegion') });
  const zipValue: string = useWatch({ control, name: getControlName('postalCode') });

  const [region, setRegion] = useState<'us' | 'international'>('us');
  const [regionInitialized, setRegionInitialized] = useState(false);
  const [domesticSuggestions, setDomesticSuggestions] = useState<SmartySuggestion[]>([]);
  const [intlCandidates, setIntlCandidates] = useState<SmartyInternationalCandidate[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState<SmartySuggestion | SmartyInternationalCandidate | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortController = useRef<AbortController | null>(null); // cancels in-flight suggestion fetches (debounced while typing)
  const hasModified = useRef(false);
  const selectedSuggestionRef = useRef<SmartySuggestion | SmartyInternationalCandidate | null>(null);

  // Initialize region from existing address once form data loads
  useEffect(() => {
    if (!regionInitialized && countryValue) {
      setRegion(DOMESTIC_CODES.has(countryValue) ? 'us' : 'international');
      setRegionInitialized(true);
    }
  }, [countryValue, regionInitialized]);

  // Clear stale suggestions and address fields when country changes
  useEffect(() => {
    setIntlCandidates([]);
    if (regionInitialized && countryValue) {
      setSelectedSuggestion(null);
      selectedSuggestionRef.current = null;
      hasModified.current = false;
      setValue(getControlName('street1'), '');
      setValue(getControlName('city'), '');
      setValue(getControlName('stateRegion'), '');
      setValue(getControlName('postalCode'), '');
    }
  }, [countryValue]);

  // Cleanup on unmount — cancels any pending debounce or in-flight fetch so they don't
  // attempt to update state after the component is gone, preventing a memory leak.
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (abortController.current) abortController.current.abort();
    };
  }, []);

  const fetchDomesticSuggestions = useCallback((query: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (query.length < 3 || !smartyEmbeddedKey) { setDomesticSuggestions([]); return; }
    debounceTimer.current = setTimeout(async () => {
      if (abortController.current) abortController.current.abort();
      abortController.current = new AbortController();
      try {
        const res = await fetch(
          `https://us-autocomplete-pro.api.smarty.com/lookup?key=${smartyEmbeddedKey}&search=${encodeURIComponent(query)}&max_results=5`,
          { signal: abortController.current.signal }
        );
        if (!res.ok) throw new Error(`Smarty US error: ${res.status}`);
        const data = await res.json();
        setDomesticSuggestions(data.suggestions ?? []);
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setDomesticSuggestions([]);
        enqueueSnackbar('Unable to load address suggestions. Please try again.', { variant: 'error' });
      }
    }, 500);
  }, [enqueueSnackbar, smartyEmbeddedKey]);

  const fetchInternationalSuggestions = useCallback((query: string, countryCode: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    const alpha3 = toAlpha3(countryCode);
    if (query.length < 3 || !smartyEmbeddedKey || !alpha3) { setIntlCandidates([]); return; }
    debounceTimer.current = setTimeout(async () => {
      if (abortController.current) abortController.current.abort();
      abortController.current = new AbortController();
      try {
        const res = await fetch(
          `https://international-autocomplete.api.smarty.com/v2/lookup?key=${smartyEmbeddedKey}&search=${encodeURIComponent(query)}&country=${alpha3}&max_results=5`,
          { signal: abortController.current.signal }
        );
        if (!res.ok) throw new Error(`Smarty International error: ${res.status}`);
        const data = await res.json();
        // Filter out candidates with no street — Smarty returns a different response format
        // for unknown/unrecognized addresses that lacks structured fields.
        setIntlCandidates((data.candidates ?? []).filter((c: SmartyInternationalCandidate) => !!c.street));
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setIntlCandidates([]);
        enqueueSnackbar('Unable to load address suggestions. Please try again.', { variant: 'error' });
      }
    }, 500);
  }, [enqueueSnackbar, smartyEmbeddedKey]);

  const handleRegionChange = useCallback((_: any, newRegion: 'us' | 'international' | null) => {
    if (!newRegion) return;
    setRegion(newRegion);
    setDomesticSuggestions([]);
    setIntlCandidates([]);
    setSelectedSuggestion(null);
    selectedSuggestionRef.current = null;
    hasModified.current = false;
    setValue(getControlName('street1'), '');
    setValue(getControlName('street2'), '');
    setValue(getControlName('city'), '');
    setValue(getControlName('stateRegion'), '');
    setValue(getControlName('postalCode'), '');
    if (newRegion === 'us') {
      setValue(getControlName('countryCode'), 'US');
      setValue(getControlName('country'), countryName('US') ?? '');
    } else {
      setValue(getControlName('countryCode'), '');
      setValue(getControlName('country'), '');
    }
  }, [getControlName, setValue]);

  return (
    <Grid container>
      {/* Region Toggle */}
      <Grid {...rowProps} xs={12}>
        <Typography variant={'h5'} className={externalStyles.fieldName}>
          Region
        </Typography>
        <ToggleButtonGroup
          value={region}
          exclusive
          onChange={handleRegionChange}
          disabled={disabled}
          sx={{ mt: '16px', mb: '24px' }}
        >
          <ToggleButton value="us">United States</ToggleButton>
          <ToggleButton value="international">International</ToggleButton>
        </ToggleButtonGroup>
      </Grid>

      {/* International: Country Selector */}
      {region === 'international' && (
        <Grid {...rowProps} xs={12}>
          <Typography variant={'h5'} className={externalStyles.fieldName}>
            Country
          </Typography>
          <CountryFormItem {...props} addressFieldName={addressFieldName} />
        </Grid>
      )}

      {/* Address Autocomplete */}
      <Grid {...rowProps} xs={12}>
        <Typography variant={'h5'} className={externalStyles.fieldName}>
          Address
        </Typography>
        <Controller
          name={getControlName('street1')}
          control={control}
          rules={{
            required: getRequiredValidationRule('address', false, required),
            maxLength: getMaxLengthValidationRule(150),
            validate: () => !hasModified.current || !!selectedSuggestionRef.current || 'Please select an address from the suggestions',
          }}
          render={({ field, fieldState }) => (
            <Autocomplete
              freeSolo
              filterOptions={(x) => x}
              options={(region === 'us' ? domesticSuggestions : intlCandidates) as (SmartySuggestion | SmartyInternationalCandidate)[]}
              getOptionLabel={(o) => typeof o === 'string' ? o : formatSuggestion(o, region === 'us')}
              // Filters guard against incomplete existing address data — users could have manually entered partial addresses before Smarty was introduced.
              inputValue={selectedSuggestion ? formatSuggestion(selectedSuggestion, region === 'us') : [field.value, cityValue, [stateValue, zipValue].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
              onInputChange={(_, value, reason) => {
                if (reason === 'reset') return;
                selectedSuggestionRef.current = null;
                setSelectedSuggestion(null);
                if (!value) {
                  hasModified.current = false;
                } else if (reason === 'input') {
                  hasModified.current = true;
                }
                field.onChange(value);
                setValue(getControlName('city'), '');
                setValue(getControlName('stateRegion'), '');
                setValue(getControlName('postalCode'), '');
                if (value && reason === 'input') {
                  region === 'us'
                    ? fetchDomesticSuggestions(value)
                    : fetchInternationalSuggestions(value, countryValue);
                }
              }}
              onChange={(_, value) => {
                if (value && typeof value !== 'string') {
                  if (region === 'us') {
                    const s = value as unknown as SmartySuggestion;
                    selectedSuggestionRef.current = s;
                    field.onChange(s.street_line);
                    setSelectedSuggestion(s);
                    setValue(getControlName('city'), s.city);
                    setValue(getControlName('stateRegion'), s.state);
                    setValue(getControlName('postalCode'), s.zipcode);
                    setValue(getControlName('countryCode'), 'US');
                    setValue(getControlName('country'), countryName('US') ?? '');
                  } else {
                    const c = value as unknown as SmartyInternationalCandidate;
                    selectedSuggestionRef.current = c;
                    field.onChange(c.street);
                    setSelectedSuggestion(c);
                    setValue(getControlName('city'), c.locality);
                    setValue(getControlName('stateRegion'), c.administrative_area);
                    setValue(getControlName('postalCode'), c.postal_code);
                    // countryCode is set by CountryFormItem. country (e.g. 'Germany') is only set when a Smarty suggestion is selected.
                    // This is intentional as SF uses countryCode to identify the country, not this field.
                    setValue(getControlName('country'), countryName(countryValue) ?? '');
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...inputProps}
                  {...params}
                  fullWidth
                  disabled={disabled}
                  placeholder="Start typing your address..."
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  className={externalStyles.inputField}
                  inputProps={{ ...params.inputProps, 'data-testid': 'street1-input' }}
                  data-testid="street1-text-field"
                />
              )}
            />
          )}
        />
      </Grid>

      {/* Apt/Unit */}
      <Grid {...rowProps} xs={12}>
        <Typography variant={'h5'} className={externalStyles.fieldName}>
          Apt, Unit, Suite, etc (Optional)
        </Typography>
        <Controller
          render={({ field }) => (
            <TextField
              {...field}
              {...inputProps}
              disabled={disabled}
              inputProps={{ 'data-testid': 'street2-input' }}
              data-testid={'street2-text-field'}
              className={externalStyles.inputField}
              FormHelperTextProps={{ 'data-testid': 'street2-helper-text' } as any}
            />
          )}
          name={getControlName('street2')}
          control={control}
          rules={{ required: getRequiredValidationRule('address', false, required) }}
        />
      </Grid>

    </Grid>
  );
};

export default AddressFormSection;
